import { useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, UploadSimple, LockSimple, WarningCircle } from "@phosphor-icons/react";
import api from "../../lib/api";
import ReportPreview from "./ReportPreview";
import { apiError, btnGhost, btnPrimary, downloadFile, inp, todayWIB } from "./util";

function ErrorList({ errors }) {
    return (
        <div className="border border-red-200 bg-red-50/60 rounded-sm p-4" data-testid="upload-errors">
            <div className="flex items-center gap-2 text-red-700 text-sm font-medium">
                <WarningCircle size={18} /> Ditemukan {errors.length} masalah — perbaiki file lalu unggah lagi
            </div>
            <ul className="mt-3 space-y-1 text-xs text-red-800 max-h-72 overflow-y-auto">
                {errors.map((e, i) => (
                    <li key={i} data-testid={`upload-error-${i}`}>
                        <span className="font-medium">{e.sheet || "File"}</span>
                        {e.row ? ` · Baris ${e.row}` : ""}
                        {e.column ? ` · ${e.column}` : ""} — {e.message}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function UploadResult({ upload, onSubmitted }) {
    const [busy, setBusy] = useState(false);
    const ok = upload.parse_status === "PARSED";
    const submit = async () => {
        if (!window.confirm("Kirim dan kunci laporan ini? Laporan tidak bisa diubah setelah dikirim.")) return;
        setBusy(true);
        try {
            await api.post(`/reporting/uploads/${upload.id}/submit`);
            toast.success("Laporan terkirim dan terkunci");
            onSubmitted();
        } catch (e) {
            toast.error(await apiError(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="space-y-6" data-testid="upload-result">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                    <span className="text-neutral-500">File:</span> <span className="font-mono">{upload.original_filename}</span>
                    <span className={`ml-3 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${ok ? "bg-sage/15 text-sage-dark" : "bg-red-50 text-red-700"}`} data-testid="upload-status">
                        {ok ? "Valid — siap dikirim" : "Tidak valid"}
                    </span>
                </div>
                {ok && (
                    <button type="button" onClick={submit} disabled={busy} className={btnPrimary} data-testid="submit-report-btn">
                        <LockSimple size={14} /> {busy ? "Mengirim…" : "Kirim & Kunci"}
                    </button>
                )}
            </div>
            {!ok && <ErrorList errors={upload.errors || []} />}
            <ReportPreview preview={upload.preview} />
        </div>
    );
}

export default function SubmitReport({ brand, onSubmitted }) {
    const [date, setDate] = useState(todayWIB());
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [upload, setUpload] = useState(null);

    const download = async () => {
        try {
            await downloadFile("/reporting/template", { brand, reporting_date: date }, "daily-sales-report.xlsx");
        } catch (e) {
            toast.error(await apiError(e));
        }
    };
    const doUpload = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("brand", brand);
            fd.append("reporting_date", date);
            fd.append("file", file);
            const { data } = await api.post("/reporting/uploads", fd);
            setUpload(data);
            if (data.parse_status !== "PARSED") toast.error("File berisi kesalahan — lihat detail di bawah");
        } catch (e) {
            toast.error(await apiError(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="space-y-6" data-testid="submit-report-panel">
            <div className="grid md:grid-cols-[180px_auto_1fr_auto] gap-3 items-end bg-white/70 border border-mist rounded-sm p-4">
                <label className="text-xs text-neutral-500">
                    Tanggal laporan
                    <input type="date" value={date} max={todayWIB()} onChange={(e) => { setDate(e.target.value); setUpload(null); }} className={`${inp} mt-1`} data-testid="report-date-input" />
                </label>
                <button type="button" onClick={download} className={btnGhost} data-testid="download-template-btn">
                    <DownloadSimple size={14} /> Template
                </button>
                <label className="text-xs text-neutral-500">
                    Template yang sudah diisi (.xlsx)
                    <input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setUpload(null); }} className={`${inp} mt-1 py-1.5`} data-testid="report-file-input" />
                </label>
                <button type="button" onClick={doUpload} disabled={!file || busy} className={btnPrimary} data-testid="upload-report-btn">
                    <UploadSimple size={14} /> {busy ? "Memeriksa…" : "Unggah & Periksa"}
                </button>
            </div>
            {upload && <UploadResult upload={upload} onSubmitted={() => { setUpload(null); setFile(null); onSubmitted(); }} />}
        </div>
    );
}
